import { useCallback } from 'react';

import { auth } from '@atlaskit/outbound-auth-flow-client/auth';
import { getATLContextUrl } from '@atlaskit/atlassian-context/get-atl-context-url';

import { cfetch } from '@confluence/network';
import { expValEquals } from '@confluence/feature-experiments';

export const useOutboundAuth = (): ((
	containerId: string,
	serviceKey: string,
	userId?: string,
) => Promise<string> | undefined) => {
	const getAccessToken = async (
		containerId: string,
		serviceKey: string,
		userId: string,
	): Promise<string> => {
		try {
			const response = await cfetch(
				`/wiki/rest/internals/1.0/word-doc/get-access-token?aaId=${userId}&containerId=${containerId}&serviceKey=${serviceKey}`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
					},
				},
			);

			const data = await response.json();

			if (!data.access_token) {
				throw new Error('Access token not found in Outbound response');
			}

			return data.access_token;
		} catch (e) {
			throw new Error(`Failed to get access token: ${e}`);
		}
	};

	return useCallback((containerId: string, serviceKey: string, userId?: string) => {
		if (!userId) return;

		if (!serviceKey.trim() || !containerId.trim()) return;

		const baseUrl = getATLContextUrl('id');
		let authUrl = `${baseUrl}/outboundAuth/start?containerId=${containerId}&serviceKey=${serviceKey}`;

		if (
			serviceKey === 'gdrive' &&
			expValEquals('cc_import_google_file_scopes', 'isEnabled', true)
		) {
			const url = new URL(authUrl);
			url.searchParams.set(
				'scopes',
				[
					'https://www.googleapis.com/auth/drive.readonly',
					'https://www.googleapis.com/auth/userinfo.email',
					'https://www.googleapis.com/auth/userinfo.profile',
				].join(' '),
			);
			authUrl = url.toString();
		}

		return auth(authUrl).then(() => {
			return getAccessToken(containerId, serviceKey, userId);
		});
	}, []);
};
